import { HttpStatusCode } from "axios";
import apiClient from "../api/ApiClient";
import { ApiEndpoints } from "../../config/constants";
import { Role, Screen, RolePermission, UserPermission, RoleWithStats, EffectivePermissions } from "../../models/DB";
import { roleManagementStore } from "../../app/stores/RoleManagement";

export const RoleManagementService = () => {
    // Get store actions - use getState() to avoid re-renders
    const { 
        updateRoles, 
        updateScreens, 
        updateUserPermissions, 
        updateRolePermissions: updateRolePermissionsInStore,
        addRole,
        updateRole: updateRoleInStore,
        removeRole,
        setLoading,
        setError
    } = roleManagementStore.getState();

    // UTILITY METHODS
    const buildEffectivePermissions = (userPermissions: UserPermission[], isPlatformAdmin: boolean = false): EffectivePermissions => {
        // Normalize screen names to lowercase for case-insensitive matching
        const allowedScreens = userPermissions
            .filter(p => p.canView)
            .map(p => p.screenName.toLowerCase());

        const screenPermissions: Record<string, any> = {};
        userPermissions.forEach(p => {
            // Use lowercase for keys to match Portal segment names
            const normalizedScreenName = p.screenName.toLowerCase();
            screenPermissions[normalizedScreenName] = {
                canView: p.canView,
                canCreate: p.canCreate,
                canUpdate: p.canUpdate,
                canDelete: p.canDelete
            };
        });

        return {
            screens: userPermissions,
            allowedScreens,
            screenPermissions,
            isPlatformAdmin
        };
    };

    // ROLE OPERATIONS
const getAllRoles = async () => {
        // Check user store state before making API call
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get<{ data: RoleWithStats[] }>(ApiEndpoints.RoleManagement.GetAllRoles);
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                updateRoles(response.data.data);
                setLoading(false);
                return;
            }
            
            setError("Failed to fetch roles");
            setLoading(false);
        } catch (error: any) {
            setError(error.message || "Failed to fetch roles");
            setLoading(false);
        }
    };

    const createRole = async (roleData: { name: string; description?: string }) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.post<{ data: Role }>(ApiEndpoints.RoleManagement.CreateRole, roleData);
            
            if (response.status === HttpStatusCode.Created && response.data.data) {
                const newRole = response.data.data;
                
                // Convert to RoleWithStats format
                const roleWithStats: RoleWithStats = {
                    roleId: newRole.id,
                    roleName: newRole.name,
                    description: newRole.description,
                    isSystem: newRole.isSystem,
                    isActive: newRole.isActive,
                    userCount: 0,
                    permissionCount: 0,
                    created: newRole.created
                };
                
                addRole(roleWithStats);
                setLoading(false);
                return newRole;
            }
            
            setError("Failed to create role");
            setLoading(false);
            return null;
        } catch (error: any) {
            setError(error.message || "Failed to create role");
            setLoading(false);
            return null;
        }
    };

    const updateRole = async (roleId: string, updates: { name?: string; description?: string; isActive?: boolean }) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.put<{ data: Role }>(ApiEndpoints.RoleManagement.UpdateRole(roleId), updates);
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                const updatedRole = response.data.data;
                
                updateRoleInStore(roleId, {
                    roleName: updatedRole.name,
                    description: updatedRole.description,
                    isActive: updatedRole.isActive
                });
                
                setLoading(false);
                return updatedRole;
            }
            
            setError("Failed to update role");
            setLoading(false);
            return null;
        } catch (error: any) {
            setError(error.message || "Failed to update role");
            setLoading(false);
            return null;
        }
    };

    const deleteRole = async (roleId: string) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.delete(ApiEndpoints.RoleManagement.DeleteRole(roleId));
            
            if (response.status === HttpStatusCode.Ok) {
                removeRole(roleId);
                setLoading(false);
                return true;
            }
            
            setError("Failed to delete role");
            setLoading(false);
            return false;
        } catch (error: any) {
            setError(error.message || "Failed to delete role");
            setLoading(false);
            return false;
        }
    };

    // SCREEN OPERATIONS
    const getAllScreens = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get<{ data: Screen[] }>(ApiEndpoints.RoleManagement.GetAllScreens);
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                updateScreens(response.data.data);
                setLoading(false);
                return;
            }
            
            setError("Failed to fetch screens");
            setLoading(false);
        } catch (error: any) {
            setError(error.message || "Failed to fetch screens");
            setLoading(false);
        }
    };

    // PERMISSION OPERATIONS
    const getRolePermissions = async (roleId: string) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get<{ data: RolePermission[] }>(ApiEndpoints.RoleManagement.GetRolePermissions(roleId));
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                updateRolePermissionsInStore(response.data.data);
                setLoading(false);
                return;
            }

            setError("Failed to fetch role permissions");
            setLoading(false);
        } catch (error: any) {
            setError(error.message || "Failed to fetch role permissions");
            setLoading(false);
        }
    };

    const updateRolePermissions = async (roleId: string, permissions: RolePermission[]) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.put(ApiEndpoints.RoleManagement.UpdateRolePermissions(roleId), {
                roleId,
                permissions: permissions.map(p => ({
                    screenId: p.screenId,
                    canView: p.canView,
                    canCreate: p.canCreate,
                    canUpdate: p.canUpdate,
                    canDelete: p.canDelete
                }))
            });
            
            if (response.status === HttpStatusCode.Ok) {
                updateRolePermissionsInStore(permissions);
                setLoading(false);
                return true;
            }
            
            setError("Failed to update role permissions");
            setLoading(false);
            return false;
        } catch (error: any) {
            setError(error.message || "Failed to update role permissions");
            setLoading(false);
            return false;
        }
    };

    // USER OPERATIONS
    const assignRoleToUser = async (userId: string, roleId: string) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.put(ApiEndpoints.RoleManagement.AssignRole, { userId, roleId });
            
            if (response.status === HttpStatusCode.Ok) {
                setLoading(false);
                return true;
            }
            
            setError("Failed to assign role to user");
            setLoading(false);
            return false;
        } catch (error: any) {
            setError(error.message || "Failed to assign role to user");
            setLoading(false);
            return false;
        }
    };

    const getMyPermissions = async () => {
        // Guard: Don't fetch if already loading
        const currentState = roleManagementStore.getState();
        if (currentState.isLoading) {
            return;
        }
        
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get<{ data: { permissions: UserPermission[]; isPlatformAdmin: boolean } }>(ApiEndpoints.RoleManagement.GetMyPermissions);
            
            if (response.status === HttpStatusCode.Ok && response.data.data) {
                // Handle multiple API response formats for backwards compatibility
                const responseData = response.data.data;
                let permissions: UserPermission[] = [];
                let isPlatformAdmin: boolean = false;

                if (Array.isArray(responseData)) {
                    // Old API format - just an array of permissions
                    permissions = responseData;
                } else if (responseData && typeof responseData === 'object') {
                    // New API format - object with permissions and isPlatformAdmin
                    // Ensure permissions is an array
                    if (Array.isArray(responseData.permissions)) {
                        permissions = responseData.permissions;
                    } else if (responseData.permissions) {
                        // If permissions exists but isn't an array, wrap it or default to empty
                        permissions = [];
                    }
                    isPlatformAdmin = responseData.isPlatformAdmin === true;
                }

                const effectivePermissions = buildEffectivePermissions(permissions, isPlatformAdmin);
                updateUserPermissions(permissions, effectivePermissions);
                setLoading(false);
                return;
            }
            console.warn('⚠️ [RoleManagementService] No permissions data in response');
            setError('Failed to fetch user permissions');
            setLoading(false);
        } catch (error: any) {  
            console.error(`🔥 [RoleManagementService] Error fetching permissions:`, {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            setError(error.message || 'Failed to fetch user permissions');
            setLoading(false);
        }
    };

    return { 
        getAllRoles,
        createRole,
        updateRole,
        deleteRole,
        getAllScreens,
        getRolePermissions,
        updateRolePermissions,
        assignRoleToUser,
        getMyPermissions
    };
}; 